import { useQuery } from "@tanstack/react-query";
import { Plane } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { usePlans } from "@/hooks/usePlans";
import { api } from "@/lib/api";
import { slug } from "@/lib/geo";
import { Avatar } from "./Avatar";
import { Overlap } from "./Overlap";

export const InviteTrip = ({ friendId, name, me, friend, compact }) => {
  const P = usePlans();
  const theirs = new Set(friend.wishlist.map((w) => w.country));
  const both = me.wishlist.map((w) => w.country).filter((c) => theirs.has(c));
  const rest = [...new Set([...friend.wishlist.map((w) => w.country), ...me.wishlist.map((w) => w.country)])].filter((c) => !both.includes(c));
  const invited = (c) => P.plans.some((p) => p.country === c && p.members.some((m) => m.user_id === friendId));
  const Row = ({ c, hot }) => (
    <button disabled={invited(c)} onClick={() => P.invite(c, { friend_id: friendId, name })} data-testid={`friend-invite-${slug(c)}`}
      className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-left hover:bg-white/[0.06] disabled:opacity-50 disabled:hover:bg-transparent transition-colors">
      <span>{c}</span>
      <span className={`font-mono text-[10px] ${hot ? "text-amber-300" : "text-slate-500"}`}>{invited(c) ? "invited" : hot ? "both want" : ""}</span>
    </button>
  );
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className={`flex items-center gap-2 rounded-full bg-sky-400 text-[#06121c] font-semibold hover:bg-sky-300 hover:-translate-y-0.5 transition-[transform,background-color] ${compact ? "px-3 py-1.5 text-xs" : "px-5 py-2.5 text-sm"}`} data-testid={compact ? `joined-plan-${friendId}` : "friend-invite-trip-button"}>
          <Plane size={compact ? 13 : 15} /> {compact ? "Plan a trip" : "Invite to plan a trip"}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-2 bg-[#131A26] border-white/10 max-h-80 overflow-y-auto" data-testid="friend-invite-popover">
        <p className="px-3 py-2 text-xs text-slate-400">Where should you go with {name}?</p>
        {both.map((c) => <Row key={c} c={c} hot />)}
        {rest.map((c) => <Row key={c} c={c} />)}
        {!both.length && !rest.length && <p className="px-3 py-4 text-sm text-slate-500">Add a country to your Want to go list first.</p>}
      </PopoverContent>
    </Popover>
  );
};

export const FriendHero = ({ friendId, friend }) => {
  const { data: me } = useQuery({ queryKey: ["state"], queryFn: api.state });
  const name = friend.owner.name.split(" ")[0];
  return (
    <section className="px-4 sm:px-8 lg:px-12 py-16 border-t border-white/5" data-testid="friend-hero">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5">
        <div className="flex items-center gap-4">
          <Avatar f={friend.owner} size="w-14 h-14" />
          <div>
            <p className="eyebrow !text-sky-300/80">You & {name}</p>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1" data-testid="friend-hero-name">Where you meet</h2>
          </div>
        </div>
        {me && <InviteTrip friendId={friendId} name={name} me={me} friend={friend} />}
      </div>
      {me && <div className="mt-8"><Overlap me={me} friend={friend} name={name} /></div>}
    </section>
  );
};
